// ── Logs / Exports / Thresholds / Tool View surfaces (Wave 10 — gold pattern) ─
// The four FINAL-wave screens, each wired via `customBody` so the route
// registration + provenance metadata is preserved (the Wave-1..9 recipe).
//   - LogsPage         (/history)             — audit/event-history + log views
//   - ExportsPage      (/exports)             — scoped CSV export (export ≤ view)
//   - ThresholdsPage   (/settings/thresholds) — LIVE GET/POST thresholds (FR-SET)
//   - ToolViewPage     (/tools)               — LIVE GET diagnostics (FR-TOOL-001)
//
// PARTICIPANT is DENIED all four (route + nav + page fail-closed). The audit log
// + event-history export are staff-only; the export type list is scoped to each
// role's view (export ≤ view). Thresholds and diagnostics preserve honest
// permission-denied, unavailable, and error states from readiness clients.

import type { ProductScreenContract } from "./product-route-contracts";
import { LogsPage } from "./logs/logs-page";
import { ExportsPage } from "./exports/exports-page";
import { ThresholdsPage } from "./settings/thresholds-page";
import { ToolViewPage } from "./tools/tool-view-page";

const WAVE10_OWNER = "VIL-20260608-0030";

function focusedDefaults(): Pick<
  ProductScreenContract,
  "requiredViewports" | "surfaceMode" | "closureState"
> {
  return {
    requiredViewports: ["desktop", "mobile"],
    surfaceMode: "focused",
    closureState: "focused"
  };
}

export function logsScreen(): ProductScreenContract {
  return {
    title: () => "Event history",
    primaryJob:
      "Read the append-only coaching event history (staff-only) and switch between the distinct log views — each a filter over the one event-history read.",
    secondaryJob:
      "Invite / report-generation / participant / session / access logs are scoped views of the same audit source; participants are denied the module.",
    workTitle: "Event history & logs",
    rows: [{ id: "event-1", action: "company_created", actor: "Operator", state: "append-only" }],
    columns: [
      { key: "action", header: "Action" },
      { key: "actor", header: "Actor" },
      { key: "state", header: "Audit state" }
    ],
    states: ["loading", "empty", "permission-denied", "unavailable", "error"],
    apiFamilies: ["history-service"],
    roleDeltas:
      "Staff read the full audit log; coach/company_admin see their scoped operational history (the real 403 surfaces as permission-denied otherwise); PARTICIPANT DENIED the module.",
    extensionSlots: ["before/after diff drawer", "saved log filters", "provider delivery history"],
    mobilePosture: "View selector, event summaries, and denied-action badges stack with no raw payloads.",
    evidenceRequirement:
      "Playwright must prove the staff audit read, the view filters, participant denial, and every honest posture.",
    vjalComponents: ["Alert","Badge","CollectionToolbar","ProTable","StatusDot"],
    localCompositions: ["history focused product surface"],
    packageGapRefs: [],
    decisionHierarchy: {
      firstViewport: "Log view selector + the append-only events table.",
      primaryAction: "Choose a log view and search the scoped events.",
      secondaryPanel: "View description + append-only / denied-action badges.",
      deferredSurface: "Diff drawer + provider delivery history land later.",
      gatedControls: "Audit table is staff-only; participant denied the module."
    },
    ...focusedDefaults(),
    closureOwner: WAVE10_OWNER,
    customBody: LogsPage
  } as ProductScreenContract;
}

export function exportsScreen(): ProductScreenContract {
  return {
    title: () => "Exports",
    primaryJob:
      "Download a CSV of the records you can view; the export type list is scoped to your role (export ≤ view) and the scope is frozen at the request.",
    secondaryJob:
      "Only CSV is produced today — Excel/Word/PDF/PowerPoint render an honest 'not available'. Participants are denied the module.",
    workTitle: "CSV exports",
    rows: [{ id: "companies", type: "companies", format: "CSV", scope: "≤ your view" }],
    columns: [
      { key: "type", header: "Export" },
      { key: "format", header: "Format" },
      { key: "scope", header: "Scope" }
    ],
    states: ["permission-denied", "error", "not-configured"],
    apiFamilies: ["export-service"],
    roleDeltas:
      "Staff export all 9 types incl. the staff-only event-history; coach/company_admin/cohort_lead export ONLY their authorized (≤ view) types; PARTICIPANT DENIED. The export type list never exceeds a role's view scope.",
    extensionSlots: ["scheduled export", "additional export formats (Excel/PDF) when the backend lands"],
    mobilePosture: "Export chooser, action, and the non-CSV not-available notice stack with no overflow.",
    evidenceRequirement:
      "Playwright must prove the scoped chooser (export ≤ view), a CSV download, the EXPORT_FAILED posture, the non-CSV honesty, and participant denial.",
    vjalComponents: ["Alert","Badge","Button","Module","ModuleBody","ModuleHead","PageFrame","ProTable","Select"],
    localCompositions: ["exports focused product surface"],
    packageGapRefs: [],
    decisionHierarchy: {
      firstViewport: "The scoped export-type chooser + the Export CSV action.",
      primaryAction: "Pick a type you can view and download its CSV.",
      secondaryPanel: "Non-CSV formats shown as honest 'not available (CSV only)'.",
      deferredSurface: "Excel/Word/PDF/PPT exports land with their backend.",
      gatedControls: "Export ≤ view per role; event-history staff-only; participant denied."
    },
    ...focusedDefaults(),
    closureOwner: WAVE10_OWNER,
    customBody: ExportsPage
  } as ProductScreenContract;
}
export function thresholdsScreen(): ProductScreenContract {
  return {
    title: () => "Thresholds & settings",
    primaryJob:
      "Manage attendance / progress / capstone thresholds (percentage 0–100) through the live settings readiness client.",
    secondaryJob:
      "GET /settings/thresholds is live. Missing rows render honestly as not set, while denied, unavailable, and error responses remain visible runtime states.",
    workTitle: "Thresholds",
    rows: [{ id: "attendance_pass", setting: "Attendance pass threshold", value: "not set" }],
    columns: [
      { key: "setting", header: "Setting" },
      { key: "value", header: "Value" }
    ],
    states: ["loading", "empty", "permission-denied", "unavailable", "error", "ready"],
    apiFamilies: ["settings-service (GET/POST /settings/thresholds)"],
    roleDeltas:
      "Staff/coach reach the admin thresholds shell; super_admin/admin can edit through the live POST contract, coach is read-only, and PARTICIPANT is denied the admin surface.",
    extensionSlots: ["additional threshold keys", "per-org defaults"],
    mobilePosture: "Live threshold fields, not-set values, and honest runtime states stack with no overflow.",
    evidenceRequirement:
      "Playwright must prove live threshold reads, staff write authorization, not-set rows, unavailable/error postures, and participant denial.",
    vjalComponents: ["Alert","Badge","Button","FormSurface","IconCog","Input"],
    localCompositions: ["settings focused product surface"],
    packageGapRefs: [],
    decisionHierarchy: {
      firstViewport: "Live threshold fields and their read posture.",
      primaryAction: "Edit and save a threshold when the role is authorized.",
      secondaryPanel: "Per-threshold descriptions and not-set values.",
      deferredSurface: "Additional threshold keys and per-org defaults land additively.",
      gatedControls: "Super-admin/admin writes; coach read-only; participant denied; runtime errors remain honest."
    },
    ...focusedDefaults(),
    closureOwner: WAVE10_OWNER,
    customBody: ThresholdsPage
  } as ProductScreenContract;
}

export function toolViewScreen(): ProductScreenContract {
  return {
    title: () => "Tool View",
    primaryJob: "A super-admin, read-only operational diagnostics surface (FR-TOOL-001) — GET /tools/diagnostics is NOW LIVE.",
    secondaryJob:
      "Renders provider-gate status, outbox health, and config flags. NO secret VALUES are shown — only states, counts, and configured/not-configured booleans.",
    workTitle: "Tool View",
    rows: [{ id: "tool-view", state: "ready", action: "view-diagnostics" }],
    columns: [
      { key: "state", header: "State" },
      { key: "action", header: "Action" }
    ],
    states: ["loading", "permission-denied", "unavailable", "error", "ready"],
    apiFamilies: ["tool-view-service (GET /tools/diagnostics, SA-only, read-only)"],
    roleDeltas: "SUPER-ADMIN ONLY reaches the diagnostics; admin/coach/company_admin/cohort_lead/PARTICIPANT all denied (fail-closed, read never fires).",
    extensionSlots: ["additional diagnostics blocks (additive)"],
    mobilePosture: "Gate / outbox / flag blocks stack vertically with no overflow.",
    evidenceRequirement:
      "Playwright must prove the SA-only reach, the rendered gate/outbox/flag diagnostics, that NO secret value is shown, and denial for every other role.",
    vjalComponents: ["Badge","Module","ModuleBody","ModuleHead","PageFrame"],
    localCompositions: ["tools focused product surface", "tool-view diagnostics sections"],
    packageGapRefs: [],
    decisionHierarchy: {
      firstViewport: "Read-only diagnostics: provider gates + outbox health + config flags.",
      primaryAction: "None — read-only diagnostics (no mutations exposed).",
      secondaryPanel: "Per-secret configured/not-configured status (neutral badges, never values).",
      deferredSurface: "Additional diagnostics blocks land additively.",
      gatedControls: "Super-admin only; no secret values and no operational mutation exposed."
    },
    ...focusedDefaults(),
    closureOwner: WAVE10_OWNER,
    customBody: ToolViewPage
  } as ProductScreenContract;
}
